import { useApolloClient } from '@apollo/client';
import { Bar, Button, Col, Divider, Icon, Justify, TextField } from '@busybusy/webapp-react-ui';
import { COST_CODES_QUERY } from 'apollo/queries/cost-code-queries';
import { SearchMagnifyingGlass } from 'assets/icons';
import classNames from 'classnames';
import { CostCodeGroupSelect } from 'components';
import EmptyState from 'components/foundation/state-templates/EmptyState/EmptyState';
import ErrorState from 'components/foundation/state-templates/ErrorState/ErrorState';
import { useDebounce, useOrganization } from 'hooks';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import ICostCode from 'types/CostCode';
import SearchType from 'types/enum/SearchType';
import { ALL_GROUPS, NO_GROUP } from 'utils/constants/stringConstants';
import { t } from 'utils/localize';
import LazyLoadCheckList from '../../../foundation/check-list/LazyLoadCheckList/LazyLoadCheckList';

export interface ICostCodeCheckListProps {
  readonly checkedItems: string[];
  enableGroups: boolean;
  enableSearch: boolean;
  onSubmit: (costCodes: string[]) => void;
  className?: string | object;
  considerProjectSpecificCostCodes?: boolean;
  projectId?: string | null;
  filteredIds?: string[];
}

const CostCodeCheckList = (props: ICostCodeCheckListProps) => {
  const {
    checkedItems,
    enableGroups,
    enableSearch,
    onSubmit,
    className,
    considerProjectSpecificCostCodes,
    projectId,
    filteredIds,
  } = props;

  const organization = useOrganization();
  const [selectedGroup, setSelectedGroup] = useState<string | null>(ALL_GROUPS);
  const [costCodes, setCostCodes] = useState<Array<ICostCode & { cursor?: string }>>();
  const [error, setError] = useState(false);

  const [sectionSize, setSectionSize] = useState<number | null>(20);

  // Local state held onto for submitting
  const [checkedIds, setCheckedIds] = useState(checkedItems);

  function onCheckAll(isChecked: boolean, toCheck: ICostCode[]) {
    if (isChecked) {
      setSectionSize(null);
      const withoutAll = toCheck.map((check) => check.id).filter((checked) => checked !== 'ALL');
      setCheckedIds([...checkedItems, ...withoutAll]);
    } else {
      setCheckedIds([]);
    }
  }

  const [checkOnLoad, setCheckOnLoad] = useState(false);

  const [loadedAll, setLoadedAll] = useState(false);
  const [search, setSearch] = useState('');
  const searchDebounced = useDebounce(search, 250);

  const client = useApolloClient();

  const headerRef = useRef<HTMLDivElement>();
  const footerRef = useRef<HTMLDivElement>();

  async function getData(after?: string): Promise<ICostCode[]> {
    return await new Promise<ICostCode[]>((res) => {
      if (considerProjectSpecificCostCodes && organization.useProjectCostCodeScoping && !projectId) {
        res([]);
      } else {
        return client
          .query<{ costCodes: ICostCode[] }>({
            query: COST_CODES_QUERY,
            fetchPolicy: 'network-only',
            variables: {
              after,
              first: 20,
              filter: {
                id: filteredIds ? { doesNotContain: filteredIds } : undefined,
                archivedOn: { isNull: true },
                search:
                  searchDebounced !== ''
                    ? {
                        type: SearchType.CONTAINS,
                        fields: ['costCode', 'title'],
                        value: searchDebounced,
                      }
                    : undefined,
                costCodeGroupId:
                  selectedGroup === ALL_GROUPS
                    ? undefined
                    : selectedGroup === NO_GROUP
                    ? { isNull: true }
                    : { equal: selectedGroup },
                projectCostCodeLink:
                  considerProjectSpecificCostCodes && organization.useProjectCostCodeScoping
                    ? { projectId: { equal: projectId }, deletedOn: { isNull: true } }
                    : undefined,
              },
            },
          })
          .then((queried) => res(queried.data.costCodes));
      }
    });
  }

  useEffect(() => {
    setLoadedAll(false);
    setCostCodes([]);
  }, [searchDebounced]);

  function didLoad(costCodeList: ICostCode[], err: boolean, loadedAllCostCodes: boolean) {
    setError(err);

    if (!err) {
      setLoadedAll(loadedAllCostCodes);
      setCostCodes(costCodeList);
    }

    if (checkOnLoad && onCheckAll) {
      onCheckAll(true, costCodeList);
    }
  }

  function onGroupSelectChange(id: string) {
    setCheckOnLoad(false);
    setCostCodes([]);
    setLoadedAll(false);
    setSelectedGroup(id);
  }

  function onSearchChange(searchString: string) {
    setCheckOnLoad(false);
    setSearch(searchString);
  }

  function handleSubmit() {
    onSubmit(checkedIds);
  }

  function setFooterRef(elt: HTMLDivElement) {
    footerRef.current = elt;
  }

  function setHeaderRef(elt: HTMLDivElement) {
    headerRef.current = elt;
  }

  function renderItem(costCode: ICostCode & { cursor?: string }) {
    return (
      <Col>
        <Bar key={costCode.id} justify={Justify.FLEX_START}>
          <span>
            {costCode.costCode} &nbsp; <em> {costCode.title}</em>
          </span>
        </Bar>
      </Col>
    );
  }

  function onCheckAllCurrentItems(isChecked: boolean) {
    if (costCodes) {
      if (isChecked) {
        setCheckOnLoad(true);
      }
      onCheckAll(isChecked, costCodes);
    }
  }

  function getMaxHeight() {
    return headerRef.current && footerRef.current
      ? `calc(100vh - ${headerRef.current.clientHeight + footerRef.current.clientHeight + 200}px)`
      : 'calc(100vh - 600px)';
  }

  const classes = classNames('cost-code-check-list', className);

  return (
    <div className={classes}>
      <div className="cost-code-check-list-header" ref={setHeaderRef}>
        {enableGroups && (
          <CostCodeGroupSelect
            className="px-6 pt-4"
            value={selectedGroup ? selectedGroup : 'ALL_GROUPS'}
            onChange={onGroupSelectChange}
          />
        )}
        {enableSearch && (
          <TextField
            className="px-6 py-4"
            onChange={onSearchChange}
            value={search}
            iconLeft={<Icon className="ml-6" svg={SearchMagnifyingGlass} />}
          />
        )}
        {(enableSearch || enableGroups) && <Divider />}
      </div>
      {error ? (
        <ErrorState />
      ) : costCodes && isEmpty(costCodes) && loadedAll ? (
        <EmptyState title={t('No Cost Codes')} />
      ) : (
        <div className="cost-code-check-list-content">
          <LazyLoadCheckList
            style={{ maxHeight: getMaxHeight() }}
            className="cost-code-check-list-lazy-loader"
            stroke={true}
            hover={true}
            data={costCodes ? costCodes : []}
            renderSize={sectionSize ?? 100}
            loadedAll={loadedAll}
            loadAll={sectionSize === null}
            getData={getData}
            renderItem={renderItem}
            didLoad={didLoad}
            offset={100}
            onCheck={setCheckedIds}
            onCheckAll={onCheckAllCurrentItems}
            isCheckedAll={
              loadedAll &&
              costCodes &&
              costCodes.every(
                (costCode) => checkedIds.some((checked) => checked === costCode.id) || costCode.id === 'ALL'
              )
            }
            checkedItems={checkedIds}
          />
        </div>
      )}
      <div className="cost-code-check-list-footer" ref={setFooterRef}>
        <Divider />
        <Button type="primary" onClick={handleSubmit} className="my-4 mx-6">
          {t('Submit')}
        </Button>
      </div>
    </div>
  );
};

export default CostCodeCheckList;
